import { t } from "@travily/i18n";
import type MemberSearchHit from "@travily/types/pending/models/MemberSearchHit";
import { CardGrid } from "@ui/components/CardGrid";
import { EmptyState } from "@ui/components/EmptyState";
import { Heading } from "@ui/components/Heading";
import { Illustration } from "@ui/components/Illustration";
import { LoadMoreTrigger } from "@ui/components/LoadMoreTrigger";
import { SkeletonBox } from "@ui/components/Skeleton";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Configure } from "react-instantsearch";
import { useSearchResults } from "@/hooks/use-search-results";
import type { MapBounds } from "@/types/search-map";
import { MemberCard } from "./components/MemberCard";
import { MembersMap } from "./MembersMap";

const BOUNDS_DEBOUNCE_MS = 250;

// Zoomed-out world views report latitudes/longitudes beyond Earth's range
// (the map repeats); OpenSearch rejects a geo_bounding_box with out-of-range
// coords ("couldn't validate latitude/longitude values"), which would surface
// as "Search is unavailable". Clamp to valid ranges before querying.
const clampLat = (value: number) => Math.max(-90, Math.min(90, value));
const clampLon = (value: number) => Math.max(-180, Math.min(180, value));

// Stable keys for the placeholder grid — avoids array-index keys without a lint disable.
const SKELETON_KEYS = ["a", "b", "c", "d", "e", "f", "g", "h"];

function GridSkeleton() {
  return (
    <CardGrid capCardWidth>
      {SKELETON_KEYS.map((key) => (
        <SkeletonBox className="aspect-square w-full rounded-3xl" key={key} />
      ))}
    </CardGrid>
  );
}

export function MembersSearchAdapter() {
  const { hits, hasMore, isError, isInitialLoading, isLoadingMore, showMore, totalCount } =
    useSearchResults<MemberSearchHit>({ sessionKey: "members" });

  // The visible map area is the only filter — there is no location search bar.
  // Panning sets `insideBoundingBox`; debounced so a drag doesn't fire per frame.
  const [boundsBox, setBoundsBox] = useState<string | null>(null);
  const [userPanned, setUserPanned] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  const handleBoundsChange = useCallback((bounds: MapBounds) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const { northEast: ne, southWest: sw } = bounds;
      setBoundsBox(`${clampLat(ne.lat)},${clampLon(ne.lng)},${clampLat(sw.lat)},${clampLon(sw.lng)}`);
      setUserPanned(true);
    }, BOUNDS_DEBOUNCE_MS);
  }, []);

  // Defence-in-depth: a member without a handle can't be linked (profilePath
  // requires it) and shouldn't be indexed (should_index? gates on setup) — drop
  // any that slip through rather than crash the grid/markers on a null handle.
  const members = useMemo(() => hits.filter((hit) => hit.handle), [hits]);

  const heading = t("components.views.members.search.heading", { count: totalCount });
  const showSkeleton = isInitialLoading && hits.length === 0;
  const empty = !isError && !showSkeleton && totalCount === 0;

  return (
    <>
      {/* Always mounted: passing `undefined` leaves the search unfiltered until the first pan. */}
      <Configure insideBoundingBox={boundsBox ?? undefined} />

      {/* Full-width map on top, card grid below. */}
      <MembersMap hits={members} onUserBoundsChange={handleBoundsChange} userPanned={userPanned} />

      <div className="flex flex-col gap-6 p-5 md:p-8">
        <Heading.Root color="primary" size="lg">
          <Heading.Title as="h1">
            {showSkeleton ? <SkeletonBox className="inline-block h-8 w-48 lg:h-11" /> : heading}
          </Heading.Title>
        </Heading.Root>
        {showSkeleton ? (
          <GridSkeleton />
        ) : isError ? (
          <EmptyState
            description={t("components.views.shared.search_results.error_description")}
            illustration={<Illustration name="trips" />}
            title={t("components.views.shared.search_results.error_title")}
          />
        ) : empty ? (
          <EmptyState
            description={t("components.views.members.search.empty_description")}
            illustration={<Illustration name="trips" />}
            title={t("components.views.members.search.empty_title")}
          />
        ) : (
          <CardGrid capCardWidth>
            {members.map((hit) => (
              <MemberCard key={hit.handle} member={hit} theme="compact" />
            ))}
          </CardGrid>
        )}
        <LoadMoreTrigger hasMore={hasMore && !isError} isLoading={isLoadingMore} onLoadMore={showMore} />
      </div>
    </>
  );
}
